import React, { useState } from "react";
import { Link } from "react-router-dom";
import { get, patch } from "../lib/api";
import { useLoad, useQueryState } from "../lib/hooks";
import { hiddenBy } from "../lib/format";
import { formatDate } from "../../helpers/datetime";
import { Button, Card, Empty, ErrorBox, Loading, Pager, Tabs } from "../ui";
import { useShell } from "../shell/context";

const KINDS = {
    contact: { label: "People", list: "People", path: "/crm/contacts/", api: "v1/crm/contacts/" },
    company: { label: "Companies", list: "Company", path: "/crm/companies/", api: "v1/crm/companies/" },
};

// Settings → Hidden (E12): records hidden from the People / Company lists,
// newest hidden first (GET /v1/crm/hidden). They are still due, searchable
// and shown everywhere else.
export default function HiddenRecords() {
    const { toast, bump, version } = useShell();
    const [kind, setKind] = useQueryState("kind", "contact");
    const [page, setPage] = useQueryState("page", 1);
    const [busy, setBusy] = useState(null);
    const k = KINDS[kind] ? kind : "contact";
    const { data, error, loading, reload } = useLoad(() => get("v1/crm/hidden", { kind: k, page, page_size: 50 }), [k, page, version]);
    const rows = (data && data.data) || [];

    const unhide = async (r) => {
        setBusy(r.id);
        try {
            await patch(KINDS[k].api + r.id, { hidden: false });
            toast(`${r.name} is back in the ${KINDS[k].list} list.`, { icon: "ri-eye-line" });
            bump();
        } catch (e) {
            toast(e.message, { error: true });
        } finally {
            setBusy(null);
        }
    };

    return (
        <Card noBody title="Hidden">
            <Tabs
                active={k}
                onChange={(v) => {
                    setKind(v);
                    setPage(1);
                }}
                tabs={Object.keys(KINDS).map((key) => ({ key, label: KINDS[key].label }))}
            />
            <div className="hz-card-section hz-small hz-muted">
                Hidden records are left out of the People and Company lists only. They keep their cadence, stay on Today and in the digests, and search still finds them.
            </div>
            <ErrorBox error={error} onRetry={reload} />
            {loading && !data ? (
                <Loading />
            ) : (
                <div className="hz-table-wrap">
                    <table className="hz-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Hidden on</th>
                                <th>Hidden by</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id}>
                                    <td><Link to={KINDS[k].path + r.id}>{r.name}</Link></td>
                                    <td className="hz-nowrap">{formatDate(r.hidden_at)}</td>
                                    <td className="hz-nowrap hz-muted">{hiddenBy(r.hidden_source)}</td>
                                    <td className="actions">
                                        <Button size="sm" variant="soft" icon="ri-eye-line" disabled={busy === r.id} onClick={() => unhide(r)}>Unhide</Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {rows.length === 0 && !error && <Empty>{k === "company" ? "No hidden companies." : "No hidden people."}</Empty>}
                </div>
            )}
            {data && <Pager page={page} maxPage={data.max_page} count={data.count} onPage={setPage} label={k === "company" ? "companies" : "people"} />}
        </Card>
    );
}
